import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Dialog } from "@/components/ui/Dialog";
import { ApiFieldErrors } from "@/components/ui/ApiFieldErrors";
import { apiFieldDescribedBy, apiFieldHasError } from "@/components/ui/api-field-errors";
import { useRenameDeployment } from "../hooks/useDeployments";

interface RenameDeploymentDialogProps {
  appName: string;
  deploymentName: string;
  open: boolean;
  onClose: () => void;
}

export function RenameDeploymentDialog({
  appName,
  deploymentName,
  open,
  onClose,
}: RenameDeploymentDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const renameDeployment = useRenameDeployment(appName);
  const [name, setName] = useState(deploymentName);
  const fieldErrorPrefix = useId();
  const nameInvalid = apiFieldHasError(renameDeployment.error, "name");

  const trimmed = name.trim();
  const canSave = trimmed !== "" && trimmed !== deploymentName;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (renameDeployment.isPending) return;
    if (!canSave) return;

    renameDeployment.mutate(
      { deploymentName, newName: trimmed },
      {
        onSuccess: ({ deployment }) => {
          onClose();
          navigate({
            to: "/apps/$appName/deployments/$deploymentName",
            params: { appName, deploymentName: deployment.name },
            replace: true,
          });
        },
      },
    );
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (renameDeployment.error) renameDeployment.reset();
  };

  const handleClose = () => {
    if (!renameDeployment.isPending) {
      setName(deploymentName);
      renameDeployment.reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} title={t("deployments.renameDeployment")}>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="deployment-rename" className="text-sm font-medium text-subtle">
            {t("deployments.deploymentNameLabel")}
          </label>
          <input
            id="deployment-rename"
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            data-autofocus
            readOnly={renameDeployment.isPending}
            aria-disabled={renameDeployment.isPending || undefined}
            aria-invalid={nameInvalid ? true : undefined}
            aria-describedby={apiFieldDescribedBy(renameDeployment.error, "name", fieldErrorPrefix)}
            className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo aria-disabled:cursor-not-allowed aria-disabled:opacity-50 hover:border-border-light"
          />
        </div>

        <ApiFieldErrors
          error={renameDeployment.error}
          fieldLabels={{ name: t("deployments.deploymentNameLabel") }}
          idPrefix={fieldErrorPrefix}
          className="mt-3"
        />

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            aria-disabled={renameDeployment.isPending || undefined}
            className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={!renameDeployment.isPending && !canSave}
            aria-disabled={renameDeployment.isPending || undefined}
            aria-busy={renameDeployment.isPending || undefined}
            className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {renameDeployment.isPending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
